import { type PanelKeys, type PanelKeyToPropsMapping } from '@databases/panel-types';
import { container } from '@databases/service-container';
import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

/**
 * React-facing helpers that wrap PanelManager state (active panel, visibility, typed data access).
 */
export default function usePanelState() {
  const manager = container.resolve('PanelManager');
  const snapshot = useSnapshot(manager.state);

  /**
   * Checks if a panel is currently visible.
   *
   * @param panelKey - Optional panel key to check. If omitted, returns true if any panel is open.
   * @returns True if the specified panel (or any panel) is visible, false otherwise.
   */
  const isPanelVisible = useCallback(
    (panelKey?: PanelKeys) => {
      if (!panelKey) {
        return snapshot.panel !== null;
      }

      if (snapshot.panel === null) {
        return false;
      }

      return snapshot.panel === panelKey;
    },
    [snapshot.panel]
  );

  /**
   * Gets the currently active panel key.
   *
   * @returns The key of the currently open panel, or null if no panel is open.
   */
  const getPanelKey = useCallback(() => {
    return snapshot.panel;
  }, [snapshot.panel]);

  /**
   * Gets the data associated with the currently open panel.
   *
   * @param _key - The panel key for type inference (not used at runtime).
   * @returns The typed data for the current panel, or undefined if no panel is open.
   */
  const getData = useCallback(
    <T extends PanelKeys>(_key: T) => {
      return snapshot.data as PanelKeyToPropsMapping[T];
    },
    [snapshot.data]
  );

  return useMemo(
    () => ({
      getData,
      getPanelKey,
      isPanelVisible
    }),
    [getData, getPanelKey, isPanelVisible]
  );
}
